import { LINK_CLASS_PRESETS } from "@/assets/utilities/tinymce/constants";
import i18n from "@/i18n";

const CUSTOM_CLASS_FIELD = "linkCustomClass";

// telHrefGuards falls back to finding the URL input by a placeholder containing "http" or "tel:",
// so no translation of this one may contain either, or an invalid phone href would be reported
// against this field instead.
const customClassPlaceholder = () => i18n.t("editor.link_dialog.custom_class_placeholder");

const PRESET_VALUES = LINK_CLASS_PRESETS.map(preset => preset.value);

const presetFor = name => PRESET_VALUES.find(value => value.toLowerCase() === name.toLowerCase());

// Namespaced so a preset can never be named after one of the dialog's own fields and overwrite it.
const presetField = value => `linkClassPreset_${value}`;

const splitClasses = value =>
  String(value ?? "")
    .split(/[\s,]+/)
    .map(name => name.replace(/^\.+/, ""))
    .filter(Boolean);

// The link plugin builds its own dialog, so it is recognised by the field only it carries.
const isLinkDialog = spec => Array.isArray(spec?.body?.items) && !!spec.initialData && "linkClass" in spec.initialData;

/**
 * Enter in the chips field commits a chip and never submits the dialog, so the tel href guard
 * has nothing to block and must let the key through.
 */
export const isLinkClassChipCommit = target =>
  target?.dataset?.srLinkClassInput === "true" && splitClasses(target.value).length > 0;

const renderChips = (container, classes, onRemove) => {
  container.textContent = "";

  classes.forEach(name => {
    const chip = document.createElement("span");
    chip.className = "sr-link-class-chip";
    chip.textContent = name;

    const remove = document.createElement("button");
    remove.type = "button";
    remove.className = "sr-link-class-chip__remove";
    remove.dataset.alloyTabstop = "true";
    remove.setAttribute("aria-label", i18n.t("editor.link_dialog.remove_class", { name }));
    remove.textContent = "×";
    remove.addEventListener("click", () => onRemove(name));

    chip.appendChild(remove);
    container.appendChild(chip);
  });
};

const mountChips = (customClasses, api) => {
  const dialogs = document.querySelectorAll(".tox-dialog");
  const dialog = dialogs[dialogs.length - 1];
  const input = dialog?.querySelector(`input.tox-textfield[placeholder="${customClassPlaceholder()}"]`);
  if (!input) return;

  input.dataset.srLinkClassInput = "true";

  const container = document.createElement("div");
  container.className = "sr-link-class-chips";
  input.closest(".tox-form__group").appendChild(container);

  const draw = () =>
    renderChips(container, customClasses, name => {
      customClasses.splice(customClasses.indexOf(name), 1);
      draw();
    });

  input.addEventListener("keydown", event => {
    if (event.key !== "Enter" || !isLinkClassChipCommit(input)) return;

    const typed = splitClasses(input.value);

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    typed.forEach(name => {
      const preset = presetFor(name);

      if (preset) {
        api.setData({ [presetField(preset)]: true });
        return;
      }

      if (!customClasses.includes(name)) customClasses.push(name);
    });
    input.value = "";
    input.dispatchEvent(new Event("input", { bubbles: true }));
    draw();
  });

  draw();
};

const buildItems = items => {
  const next = [...items];

  next.push({
    type: "label",
    label: i18n.t("editor.link_dialog.class"),
    items: [
      ...LINK_CLASS_PRESETS.map(preset => ({
        type: "checkbox",
        name: presetField(preset.value),
        label: i18n.t(preset.labelKey),
      })),
      {
        type: "input",
        name: CUSTOM_CLASS_FIELD,
        label: i18n.t("editor.link_dialog.custom_class"),
        placeholder: customClassPlaceholder(),
      },
    ],
  });

  return next;
};

const withLinkFields = spec => {
  const originalClasses = splitClasses(spec.initialData.linkClass);
  const customClasses = originalClasses.filter(name => !presetFor(name));
  const submit = spec.onSubmit;

  const nextSpec = {
    ...spec,
    body: { ...spec.body, items: buildItems(spec.body.items) },
    initialData: {
      ...spec.initialData,
      ...LINK_CLASS_PRESETS.reduce(
        (presets, preset) => ({
          ...presets,
          [presetField(preset.value)]: originalClasses.some(name => presetFor(name) === preset.value),
        }),
        {},
      ),
      [CUSTOM_CLASS_FIELD]: "",
    },
    onSubmit: api => {
      const data = api.getData();
      const selected = [
        ...LINK_CLASS_PRESETS.filter(preset => data[presetField(preset.value)]).map(preset => preset.value),
        ...customClasses,
        ...splitClasses(data[CUSTOM_CLASS_FIELD]).map(name => presetFor(name) ?? name),
      ];
      const kept = originalClasses.filter(name => selected.includes(name));
      const linkClass = [...new Set([...kept, ...selected])].join(" ");

      submit({ ...api, getData: () => ({ ...data, linkClass }) });
    },
  };

  return { spec: nextSpec, customClasses };
};

/**
 * Adds preset and free-text class fields to the built-in Insert link dialog.
 *
 * The plugin's dialog spec is not configurable beyond `link_class_list`, which is a single
 * select, so the spec is extended on its way to the window manager instead. The chips are
 * built in the DOM afterwards because the dialog components cannot render a growing list
 * without a redial, which would drop what the user has typed into the other fields.
 */
export function installLinkDialogFields(editor) {
  editor.on("init", () => {
    const { windowManager } = editor;
    const open = windowManager.open;

    windowManager.open = function (spec, params) {
      if (!isLinkDialog(spec)) return open.call(this, spec, params);

      const { spec: nextSpec, customClasses } = withLinkFields(spec);
      const dialog = open.call(this, nextSpec, params);

      mountChips(customClasses, dialog);

      return dialog;
    };
  });
}
